<template>
	<widget-settings-layout ref="layout" v-if="settings" slug="wordCloud" name="Word cloud" :error="saveError" :saving="saving" :has-unsaved-changes="hasUnsavedChanges" :settings="settings" :saved-settings-json="savedSettingsJson" @save="save">
		<b-card title="Words" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Max words">
						<b-form-input type="number" min="1" max="500" v-model.number="settings.maxWords"></b-form-input>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Refresh interval">
						<b-input-group append="min">
							<b-form-input type="number" min="1" v-model.number="settings.refreshInterval"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Layout" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Spiral">
						<b-form-select v-model="settings.spiral" :options="spiralOptions"></b-form-select>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Scale">
						<b-form-select v-model="settings.scale" :options="scaleOptions"></b-form-select>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Orientations">
						<b-form-input type="number" min="1" max="8" v-model.number="settings.rotationOrientations"></b-form-input>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Min angle">
						<b-input-group append="°">
							<b-form-input type="number" min="-90" max="90" v-model.number="settings.rotationMinAngle"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Max angle">
						<b-input-group append="°">
							<b-form-input type="number" min="-90" max="90" v-model.number="settings.rotationMaxAngle"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Font sizes" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Min font size">
						<b-input-group append="px">
							<b-form-input type="number" min="1" v-model.number="settings.minFontSize"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Max font size">
						<b-input-group append="px">
							<b-form-input type="number" min="1" v-model.number="settings.maxFontSize"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<text-settings :settings="settings" :no-align="true" />
		<b-card title="Colors" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Text color">
						<color-picker v-model="settings.textColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Outline" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Blur radius">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="100" step="any" v-model="settings.blurRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Outline size">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="10" step="any" v-model="settings.spreadRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="6">
					<b-form-group label="Outline color">
						<color-picker v-model="settings.shadowColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card class="mb-3">
			<b-form-group label="Extra CSS" class="mb-0">
				<b-textarea v-model="settings.extraCSS"></b-textarea>
			</b-form-group>
		</b-card>
		<template #preview>
			<word-cloud-widget-preview :settings="settings || {}" />
		</template>
	</widget-settings-layout>
</template>

<script>
import ColorPicker from '@/components/ColorPicker.vue';
import WidgetSettingsLayout from '@/components/Widget/WidgetSettingsLayout.vue';
import TextSettings from '@/components/Widget/TextSettings.vue';
import WordCloudWidgetPreview from '@/components/Widget/WordCloudWidgetPreview.vue';

export default {
	components: {
		ColorPicker,
		WidgetSettingsLayout,
		TextSettings,
		WordCloudWidgetPreview,
	},
	beforeRouteLeave(to, from, next) {
		if (!this.hasUnsavedChanges) return next();
		this.$refs.layout.showLeaveModal(next, () => this.save());
	},
	data() { return {
		loading: 0,
		error: null,
		saving: 0,
		saveError: null,
		savedSettingsJson: null,
		settings: null,
		spiralOptions: [
			{ value: 'archimedean', text: 'Archimedean' },
			{ value: 'rectangular', text: 'Rectangular' },
		],
		scaleOptions: [
			{ value: 'sqrt',   text: 'Square root' },
			{ value: 'linear', text: 'Linear' },
			{ value: 'log',    text: 'Logarithmic' },
		],
	}; },
	async mounted() {
		window.addEventListener('beforeunload', this._onBeforeUnload);
		try {
			this.loading++;
			this.error = null;
			this.saveError = null;
			const s = await this.$api.get('widgetSettings/word-cloud');
			this.settings = {
				maxWords:             s.maxWords             || 50,
				refreshInterval:      s.refreshInterval      || 10,
				spiral:               s.spiral               || 'archimedean',
				scale:                s.scale                || 'sqrt',
				rotationOrientations: s.rotationOrientations || 2,
				rotationMinAngle:     s.rotationMinAngle     ?? 0,
				rotationMaxAngle:     s.rotationMaxAngle     ?? -90,
				minFontSize:          s.minFontSize          || 14,
				maxFontSize:          s.maxFontSize          || 80,
				textColor:            s.textColor            || 'rgba(255, 255, 255, 1)',
				fontFamily:           s.fontFamily           || '',
				letterSpacing:        s.letterSpacing        || 0,
				boldText:             s.boldText             || false,
				italicText:           s.italicText           || false,
				underlineText:        s.underlineText        || false,
				strikethroughText:    s.strikethroughText    || false,
				uppercaseText:        s.uppercaseText        || false,
				blurRadius:           s.blurRadius           || 0,
				spreadRadius:         s.spreadRadius         || 0,
				shadowColor:          s.shadowColor          || 'rgba(0, 0, 0, 1)',
				extraCSS:             s.extraCSS             || '',
				customFont:           s.customFont           || false,
			};
			this.savedSettingsJson = JSON.stringify(this.settings);
		} catch(e) {
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	computed: {
		hasUnsavedChanges() {
			if (this.savedSettingsJson === null) return false;
			return JSON.stringify(this.settings) !== this.savedSettingsJson;
		},
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this._onBeforeUnload);
	},
	methods: {
		_onBeforeUnload(e) {
			if (this.hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = '';
			}
		},
		async save() {
			try {
				this.saving++;
				this.saveError = null;
				await this.$api.post('widgetSettings/word-cloud', { ...this.settings });
				this.savedSettingsJson = JSON.stringify(this.settings);
			} catch(err) {
				console.error('Error saving widget:', err);
				this.saveError = err;
			} finally {
				this.saving--;
			}
		},
	},
};
</script>
